/**
 * Screen Wake Lock — keep the device awake while the app is in the foreground.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
 * iOS throttles (and eventually suspends) a web app's JavaScript the moment the
 * screen locks. That is the root of every "playback stalls / controls dead /
 * time frozen when locked" problem: the OS keeps the audio going but freezes
 * the JS layer around it. The Screen Wake Lock API (`navigator.wakeLock`,
 * supported on iOS Safari 16.4+ and in installed PWAs) asks the OS to keep the
 * display on, so the screen never auto-locks while the user is in the app —
 * which sidesteps the throttle entirely rather than fighting it.
 *
 * ── TWO HARD RULES OF THE API, BOTH EASY TO GET WRONG ──────────────────────
 * 1. AUTO-RELEASE ON HIDE. A screen wake lock is released whenever the page
 *    becomes hidden (tab switch, app backgrounded, screen turned off). It MUST
 *    therefore be re-requested on every `visibilitychange` back to visible — a
 *    lock acquired once and never re-requested silently lapses the first time
 *    the user glances away.
 *
 * 2. SAFARI REQUIRES USER ACTIVATION. WebKit only grants the lock to a request
 *    made in the context of a user gesture; a request fired at page load is
 *    rejected outright (`NotAllowedError`). This is why "keep the screen awake"
 *    appeared to do nothing on iPhone even though the code ran: the app asked
 *    for the lock during boot, before the user had touched anything, and the
 *    single refusal was swallowed. So the DESIRE is now separated from the
 *    ACQUISITION — {@link WakeLockController.setDesired} records the intent, and
 *    the lock is claimed at the first opportunity that can actually succeed: a
 *    real user gesture, or a return to visibility. The gesture listeners stay
 *    installed, so a refusal is simply retried on the user's next tap rather
 *    than being final.
 *
 * (For the record on platform support: the API needs iOS 16.4+, and it did not
 * work in an INSTALLED Home Screen web app until iOS 18.4 — WebKit bug 254545,
 * resolved 2025-03-31. Below that, no amount of correct calling helps.)
 *
 * ── BEST-EFFORT, NEVER FATAL ───────────────────────────────────────────────
 * The API is absent on older engines and the request can reject (low battery,
 * power-save mode, OS policy). Every failure is swallowed: a device that cannot
 * hold the lock simply locks as it always did. What is NOT swallowed any more is
 * the OBSERVABILITY — {@link WakeLockController.status} reports what is actually
 * true, so "it doesn't seem to work" can be answered with a fact instead of a
 * guess (see the Settings screen's screen-awake row).
 *
 * @module
 */

/** The slice of `navigator.wakeLock` this controller drives. */
export interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

export interface WakeLockApiLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

export interface WakeLockDeps {
  /** `navigator.wakeLock`, or undefined where unsupported. */
  readonly api?: WakeLockApiLike;
  /**
   * Registers the visibility + gesture listeners — `document` in production.
   * Typed with a plain `string` event name because this now listens for the
   * user-activation events too (see rule 2 in the module header).
   */
  readonly doc?: {
    addEventListener(type: string, listener: () => void, options?: unknown): void;
    removeEventListener(type: string, listener: () => void): void;
    readonly visibilityState: string;
  };
  /** Local diagnostics; never user-facing. */
  readonly warn?: (message: string, detail: Record<string, string | number>) => void;
}

/**
 * What is actually true right now — the honest answer for a diagnostics row.
 *
 * `waiting` is the state Safari puts us in between "the app wants the lock" and
 * "the user has touched the screen once", and it is the one that used to be
 * invisible.
 */
export type WakeLockState = 'unsupported' | 'off' | 'held' | 'waiting';

export interface WakeLockStatus {
  readonly state: WakeLockState;
  /** The `name` of the last rejection (e.g. `NotAllowedError`), if any. */
  readonly lastError: string | null;
}

export interface WakeLockController {
  /**
   * Declare whether the app WANTS the screen kept awake. `true` claims the lock
   * as soon as the platform will allow it — immediately where no user gesture is
   * required, otherwise at the first gesture — and re-claims it on every return
   * to visibility. `false` releases it. Idempotent.
   */
  setDesired(desired: boolean): void;
  /** True while a lock is actually held (for tests / diagnostics). */
  isHeld(): boolean;
  /** What is actually true, for the Settings diagnostics row. */
  status(): WakeLockStatus;
  dispose(): void;
}

/**
 * Events that carry user activation on the platforms that demand it. `touchend`
 * and `click` rather than only `pointerdown`, because WebKit is strictest about
 * what counts and a tap should not be missed.
 */
const GESTURES = ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'] as const;

function resolveApi(deps: WakeLockDeps): WakeLockApiLike | undefined {
  if (deps.api !== undefined) return deps.api;
  const nav = (globalThis as { navigator?: { wakeLock?: WakeLockApiLike } }).navigator;
  return nav?.wakeLock;
}

function resolveDoc(deps: WakeLockDeps): WakeLockDeps['doc'] | undefined {
  if (deps.doc !== undefined) return deps.doc;
  return typeof document === 'undefined' ? undefined : document;
}

export function createWakeLock(deps: WakeLockDeps = {}): WakeLockController {
  const api = resolveApi(deps);
  const doc = resolveDoc(deps);
  const warn = deps.warn ?? (() => {});

  let desired = false;
  let sentinel: WakeLockSentinelLike | null = null;
  /** Guards against two overlapping `request()` calls racing to hold two locks. */
  let acquiring = false;
  /** The `name` of the last rejection, surfaced by {@link status}. */
  let lastError: string | null = null;

  function visible(): boolean {
    return doc === undefined || doc.visibilityState === 'visible';
  }

  async function acquire(): Promise<void> {
    if (api === undefined || sentinel !== null || acquiring || !desired || !visible()) return;
    acquiring = true;
    try {
      const held = await api.request('screen');
      // A newer intent may have landed while we awaited — honour it.
      if (!desired || !visible()) {
        void held.release().catch(() => {});
        return;
      }
      sentinel = held;
      lastError = null;
      // The OS releases the lock on hide; drop our reference so the next
      // visibility flip re-acquires rather than believing it still holds one.
      held.addEventListener('release', () => {
        if (sentinel === held) sentinel = null;
      });
    } catch (error) {
      // Expected on WebKit until the user has touched the screen — the gesture
      // listeners below will retry, so this is a step in the normal path, not a
      // dead end. Recorded rather than only logged (see `status`).
      lastError = (error as Error)?.name ?? 'unknown';
      warn('wake-lock: request rejected', { reason: lastError });
    } finally {
      acquiring = false;
    }
  }

  function release(): void {
    const held = sentinel;
    sentinel = null;
    if (held !== null) void held.release().catch(() => {});
  }

  const onVisibility = (): void => {
    if (visible()) {
      void acquire();
      return;
    }
    // On hide the OS auto-releases anyway; releasing explicitly keeps our own
    // `sentinel` reference honest so the next show() re-acquires cleanly.
    release();
  };
  doc?.addEventListener('visibilitychange', onVisibility);

  /*
   * THE FIX for "always-on does nothing on iPhone" (module header, rule 2).
   * WebKit grants the lock only to a request made under user activation, so the
   * first real gesture is the first moment the ask can succeed. These stay
   * registered for the controller's lifetime: `acquire` no-ops while a lock is
   * already held, so the cost is one comparison per tap, and a refusal (power
   * save, policy) is retried on the next tap instead of being permanent.
   */
  const onGesture = (): void => {
    if (desired && sentinel === null) void acquire();
  };
  for (const type of GESTURES) {
    doc?.addEventListener(type, onGesture, { passive: true, capture: true });
  }

  return {
    setDesired(next) {
      desired = next;
      // Try immediately: engines with no activation requirement (Chromium,
      // Firefox) grant it here, so nothing waits for a tap it does not need.
      if (next) void acquire();
      else release();
    },
    isHeld() {
      return sentinel !== null;
    },
    status() {
      if (api === undefined) return { state: 'unsupported', lastError };
      if (sentinel !== null) return { state: 'held', lastError };
      return { state: desired ? 'waiting' : 'off', lastError };
    },
    dispose() {
      desired = false;
      doc?.removeEventListener('visibilitychange', onVisibility);
      for (const type of GESTURES) doc?.removeEventListener(type, onGesture);
      release();
    },
  };
}
